import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, ExternalLink, Calendar, ChevronRight, Inbox,
} from 'lucide-react';
import { Card } from '../../ui-v2';

export default function BloqueCampanasActivas({ campanas }) {
  const navigate = useNavigate();

  return (
    <Card className="overflow-hidden p-0 mb-6">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium text-slate-900">Campañas activas</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {campanas.length} {campanas.length === 1 ? 'campaña en curso' : 'campañas en curso'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/citas-tecnicas/campanas')}
          className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
        >
          Ver todas
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {campanas.length === 0 ? (
        <div className="px-5 py-12 text-center">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">
            No hay campañas activas en este momento.
          </p>
          <button
            type="button"
            onClick={() => navigate('/citas-tecnicas/campanas/nueva')}
            className="mt-3 text-xs text-primary hover:underline"
          >
            Crear una nueva campaña →
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {campanas.map((c) => (
            <FilaCampana
              key={c.id}
              campana={c}
              onClick={() => navigate(`/citas-tecnicas/campanas/${c.id}/resumen`)}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function FilaCampana({ campana, onClick }) {
  const u = campana.usuarios;
  const dr = campana.dias_restantes;
  const venceProximo = dr !== null && dr <= 14 && dr >= 0;
  const vencida = dr !== null && dr < 0;

  return (
    <li
      onClick={onClick}
      className="px-5 py-4 hover:bg-slate-50/60 cursor-pointer transition-colors"
    >
      <div className="flex items-start gap-4">
        {/* Ámbito */}
        <div className="w-7 h-7 rounded-full bg-slate-100 grid place-items-center shrink-0 mt-0.5">
          {campana.ambito === 'colaborador' ? (
            <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
          ) : (
            <Building2 className="w-3.5 h-3.5 text-slate-500" />
          )}
        </div>

        {/* Datos */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium text-slate-900 truncate">
              {campana.titulo}
            </div>
            <span className="text-[10px] text-slate-400 uppercase tracking-wide">
              {campana.ambito === 'colaborador' ? 'Colaboradores' : 'Internos'}
            </span>
          </div>

          {/* Barra de progreso */}
          <div className="mt-2 flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden max-w-xs">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${campana.progreso_pct}%` }}
              />
            </div>
            <span className="text-xs text-slate-600 tabular-nums shrink-0">
              {campana.progreso_pct}%
            </span>
          </div>

          {/* Contadores */}
          <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span>{u.total} usuarios</span>
            {u.pendientes > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-amber-600">{u.pendientes} sin cita</span>
              </>
            )}
            {u.con_cita > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-emerald-600">{u.con_cita} con cita</span>
              </>
            )}
            {u.realizado > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-primary font-medium">{u.realizado} completados</span>
              </>
            )}
          </div>
        </div>

        {/* Fecha límite */}
        <div className="shrink-0 text-right">
          <div className="text-[10px] text-slate-400 uppercase tracking-wide flex items-center gap-1 justify-end">
            <Calendar className="w-3 h-3" />
            Hasta
          </div>
          <div className={
            'text-sm tabular-nums ' +
            (vencida ? 'text-red-600' : venceProximo ? 'text-amber-600' : 'text-slate-700')
          }>
            {formatFechaCorta(campana.fecha_limite)}
          </div>
          {dr !== null && (
            <div className={
              'text-[10px] mt-0.5 ' +
              (vencida ? 'text-red-500' : venceProximo ? 'text-amber-600' : 'text-slate-400')
            }>
              {vencida
                ? `Vencida hace ${Math.abs(dr)}d`
                : dr === 0
                  ? 'Vence hoy'
                  : `${dr} día${dr === 1 ? '' : 's'} restantes`}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function formatFechaCorta(s) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short',
  });
}
